/**
 * Toast — composant éphémère stackable pour les feedbacks discrets
 * (Phase 3.3.4).
 *
 * API :
 *
 *   - `mountToastHost()` : appelé 1× au boot par `main.ts`. Crée un
 *     container hors AppShell (sur `document.body`) qui survit aux
 *     re-renders d'AppShell, à l'image d'`UpdateBanner`.
 *   - `showToast({message, kind, durationMs})` : pousse un toast.
 *     Retourne un id permettant un `dismissToast(id)` programmatique
 *     (rarement utilisé — le toast s'auto-dismiss).
 *
 * Cycle de vie d'un toast :
 *
 *   1. `showToast(...)` → push dans `state.toasts`, rerender.
 *   2. requestAnimationFrame → toggle `.visible` (transition CSS
 *      opacité + translate).
 *   3. setTimeout(durationMs) → remove du state, rerender. Le DOM
 *      node disparaît (pas de transition de sortie en 3.3.4 — c'est
 *      OK pour un toast qui s'est déjà fait remarquer 4 s).
 *
 * Ton bienveillant (CLAUDE.md §2.1) — pas de message anxiogène. Les
 * libellés sont fournis par le caller via i18n.
 */
import { html, render, type TemplateResult } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map.js';
import styles from './Toast.module.css';
import { Icon, Icons } from './icons.js';

export type ToastKind = 'info' | 'success' | 'error';

export interface ShowToastOptions {
  /** Message à afficher — déjà traduit côté caller. */
  message: string;
  /** Variante visuelle (border-left coloré). Default `'info'`. */
  kind?: ToastKind;
  /** Durée d'affichage en ms. Default 4000. */
  durationMs?: number;
  /** Bouton d'action optionnel (ex. « Annuler » pour un undo). */
  action?: { label: string; onAction: () => void };
}

interface ActiveToast {
  id: number;
  message: string;
  kind: ToastKind;
  action?: { label: string; onAction: () => void };
  /** True quand le DOM node a fini son enter transition. */
  visible: boolean;
}

interface ToastState {
  toasts: ActiveToast[];
}

const state: ToastState = { toasts: [] };
let container: HTMLElement | null = null;
let nextId = 1;

function toastTemplate(t: ActiveToast): TemplateResult {
  const classes = {
    [styles.toast!]: true,
    [styles.visible!]: t.visible,
    [styles.info!]: t.kind === 'info',
    [styles.success!]: t.kind === 'success',
    [styles.error!]: t.kind === 'error',
  };
  // Icône par kind — pas anxiogène (CLAUDE.md §2.1). Pour 'info' on
  // utilise spark (cohérent UpdateBanner), pour success on check
  // (validation), pour error on bell (attention douce, pas alarme).
  const iconName =
    t.kind === 'success' ? Icons.check : t.kind === 'error' ? Icons.bell : Icons.spark;
  return html`<div
    class=${classMap(classes)}
    role=${t.kind === 'error' ? 'alert' : 'status'}
    data-testid="toast"
    data-toast-kind=${t.kind}
  >
    <span class=${styles.toastIcon} aria-hidden="true">${Icon(iconName, 16, { stroke: 1.6 })}</span>
    <span class=${styles.toastMessage}>${t.message}</span>
    ${t.action
      ? html`<button
          type="button"
          class=${styles.toastAction}
          data-testid="toast-action"
          @click=${(): void => {
            t.action?.onAction();
            dismissToast(t.id);
          }}
        >
          ${t.action.label}
        </button>`
      : null}
  </div>`;
}

function rerender(): void {
  if (!container) return;
  render(
    html`<div class=${styles.host}>${state.toasts.map((t) => toastTemplate(t))}</div>`,
    container,
  );
}

/**
 * Monte le host des toasts dans `document.body`. Idempotent —
 * appels multiples retournent silencieusement.
 */
export function mountToastHost(): () => void {
  if (container) return () => undefined;
  container = document.createElement('div');
  container.setAttribute('data-testid', 'toast-host');
  document.body.appendChild(container);
  rerender();
  return () => {
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
    container = null;
    state.toasts = [];
  };
}

/**
 * Pousse un toast. Retourne son id (utilisable pour `dismissToast`).
 */
export function showToast(options: ShowToastOptions): number {
  const id = nextId++;
  const toast: ActiveToast = {
    id,
    message: options.message,
    kind: options.kind ?? 'info',
    visible: false,
  };
  state.toasts.push(toast);
  rerender();

  // Petite tick pour permettre au DOM d'enregistrer le node, puis
  // toggle `.visible` pour déclencher la transition d'entrée.
  // `requestAnimationFrame` est plus fiable que `setTimeout(0)`
  // pour ça (synchronisé sur le repaint).
  const raf = typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame : setTimeout;
  raf(() => {
    toast.visible = true;
    rerender();
  });

  const durationMs = options.durationMs ?? 4000;
  setTimeout(() => {
    dismissToast(id);
  }, durationMs);

  return id;
}

/**
 * Retire un toast par son id (programmatique). No-op si déjà retiré.
 */
export function dismissToast(id: number): void {
  const before = state.toasts.length;
  state.toasts = state.toasts.filter((t) => t.id !== id);
  if (state.toasts.length !== before) {
    rerender();
  }
}

/** @internal pour tests — purge et reset. */
export function _resetToastsForTests(): void {
  state.toasts = [];
  if (container && container.parentNode) {
    container.parentNode.removeChild(container);
  }
  container = null;
  nextId = 1;
}
